// The free QR tool's live redraw. Progressive enhancement, and nothing on the
// page depends on it: with this file blocked, the form submits, the server
// draws, and the "Update code" button is what it always was.
//
// ---------------------------------------------------------------------------
// WHY THIS IS A FILE AND NOT A <script> BLOCK IN Tools.razor, 2026-08-08
// ---------------------------------------------------------------------------
// **Because the inline one never ran, and had never run since it was written.**
// It was a 250ms debounce that repainted the preview as you typed, it was
// verified by looking at the page, and the thing it was doing - re-rendering an
// image the server had already drawn - is invisible when it stops. Two separate
// mechanisms killed it, and either alone is enough:
//
// 1. **The page is `ServerPrerendered`.** The component's DOM is rendered by
//    the server, parsed by the browser, and then THROWN AWAY and rebuilt when
//    the circuit connects. A script that ran at parse time and captured
//    `#qr-form` is left holding a node that is no longer in the document; its
//    listeners are attached to an orphan and nothing it does is visible.
//    Measured, not assumed: a marker written onto the form at `DOMContentLoaded`
//    is gone by the time the circuit settles, and `DOMDebugger.getEventListeners`
//    on the live form reported none. PLAN.md, "I-83"; the same re-render is
//    I-81 on the Ampulla side, where it costs two orphaned intervals instead.
// 2. **Blazor re-inserts the component's markup, including inline script text,
//    and the text arrives in fragments.** The browser tries to compile each
//    piece, and every load threw `SyntaxError: Invalid or unexpected token` out
//    of `appendChild` inside `blazor.server.js`. An external `<script src>` has
//    no text to fragment.
//
// **So this file survives both, and the two defences are separate:**
// `document` is never replaced, so listening THERE and looking the elements up
// at use time makes DOM replacement irrelevant; and the guard below makes a
// second execution - which is exactly what I-81 records happening to the
// Ampulla runtime - a no-op rather than a doubled listener.
//
// **It knows nothing about the design system.** The two class names it swaps
// between are handed to it by the markup, because Tailwind only generates
// classes it can SEE and it does not scan this file. A literal `text-error` in
// here would be purged from app.css and the message would render unstyled, on a
// green build.

(function () {
    'use strict';

    if (window.__qrTool) { return; }

    window.__qrTool = true;

    // The circuit's window on the truth. Blazor's NavigationManager never
    // hears history.replaceState, so the server-side page still holds the URL
    // the circuit CONNECTED with - asking the browser is the only honest read
    // of what the form has become since. The save button calls this.
    window.qrToolQuery = function () { return location.search; };

    var wait;
    var flight;
    var drawn;

    // Looked up on every use, never captured. See the note above.
    function parts() {
        var form = document.getElementById('qr-form');

        if (!form) { return null; }

        var found = {
            form: form,
            root: form.getAttribute('data-qr-root') || '',
            image: document.getElementById('qr-preview'),
            built: document.getElementById('qr-result'),
            problem: document.getElementById('qr-problem'),
            message: document.getElementById('qr-message'),
            pending: document.getElementById('qr-pending'),
            png: document.getElementById('qr-png'),
            svg: document.getElementById('qr-svg'),
        };

        for (var key in found) {
            if (!found[key]) { return null; }
        }

        return found;
    }

    function show(page, blob) {
        // Released before the next one replaces it. Object URLs live as long as
        // the document, so a tool somebody fiddles with for ten minutes would
        // otherwise hold on to every code it ever drew.
        if (drawn) { URL.revokeObjectURL(drawn); }

        drawn = URL.createObjectURL(blob);
        page.image.src = drawn;
        page.image.hidden = false;
        page.pending.hidden = true;
        page.built.hidden = false;
        page.problem.hidden = true;
    }

    // The third state: the form is valid and the picture does not exist yet -
    // a new Editable code has no symbol until saving mints it, and this page
    // never shows a QR that scans to nothing. The save button lives in the
    // built panel, so that panel stays; only the picture yields its place.
    function showPending(page) {
        page.image.hidden = true;
        page.pending.hidden = false;
        page.built.hidden = false;
        page.problem.hidden = true;
    }

    function explain(page, text, incomplete) {
        page.message.textContent = text;
        page.message.className = page.problem.getAttribute(incomplete ? 'data-qr-quiet' : 'data-qr-loud') || '';
        page.built.hidden = true;
        page.problem.hidden = false;
    }

    function update() {
        var page = parts();

        if (!page) { return; }

        var query = new URLSearchParams(new FormData(page.form)).toString();

        // The filled-in state IS the URL - reloadable, bookmarkable, shareable.
        // Drawing without submitting would have quietly retired that.
        history.replaceState(null, '', page.root + '?' + query);

        page.png.href = page.root + '/qr.png?' + query;
        page.svg.href = page.root + '/qr.svg?' + query;

        // The sign-in link's returnUrl must carry what the visitor has typed
        // SINCE the page rendered, or crossing the login loses it - the server
        // rendered that href from the URL as it stood at connect time, for the
        // same reason the save button cannot trust Navigation.Uri. Optional:
        // the anchor only exists signed out, and parts() must not go inert
        // over it.
        var signin = document.getElementById('qr-signin');

        if (signin) {
            signin.href = (signin.getAttribute('data-qr-login') || '')
                + encodeURIComponent(page.root + '?' + query);
        }

        // A slower earlier request must not land on top of a newer one and show
        // a code for a form that has since changed.
        if (flight) { flight.abort(); }

        flight = new AbortController();

        fetch(page.root + '/qr.png?' + query, { signal: flight.signal })
            .then(function (response) {
                if (response.ok) {
                    return response.blob().then(function (blob) { show(page, blob); });
                }

                // Not a refusal: the form is fine and the picture is simply
                // not minted yet. Keep the built panel (the save button lives
                // there) and put the words where the picture would be.
                if (response.headers.get('X-Qr-Pending') === '1') {
                    showPending(page);
                    return;
                }

                // **Fetched rather than assigned to `src`, and this is the
                // reason.** An `<img>` whose request 400s shows a torn page and
                // cannot read a word of the reply - so the refusal with the most
                // reasoning behind it, the contrast floor, was the one nobody
                // could see. The server's own sentence goes in the panel.
                var incomplete = response.headers.get('X-Qr-Incomplete') === '1';

                return response.text().then(function (text) { explain(page, text, incomplete); });
            })
            .catch(function (error) {
                if (error.name === 'AbortError') { return; }

                explain(page, 'That could not be drawn just now. Press Update code to try again.', false);
            });
    }

    // **One request per edit, not one per keystroke.** A colour input fires
    // while it is dragged and a text field fires per character, so the wait is
    // what makes this affordable rather than what makes it feel slow.
    function schedule(event) {
        var target = event.target;

        if (!target || !target.closest || !target.closest('#qr-form')) { return; }

        clearTimeout(wait);
        wait = setTimeout(update, 1000);
    }

    // `change` as well as `input`: a <select> and a checkbox report only the
    // first in some browsers, and the appearance controls are both.
    document.addEventListener('input', schedule);
    document.addEventListener('change', schedule);
})();
